import { useState, useEffect, useRef } from 'react';

// Cache global pour les images
const imageCache = new Map<string, boolean>();

interface UseLazyImageOptions {
  src: string;
  fallbackSrc: string;
  rootMargin?: string;
  threshold?: number;
}

export const useLazyImage = ({ 
  src, 
  fallbackSrc, 
  rootMargin = '50px',
  threshold = 0.1 
}: UseLazyImageOptions) => {
  const [imageSrc, setImageSrc] = useState<string>(fallbackSrc);
  const [isLoading, setIsLoading] = useState(true);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Vérifier si l'image est déjà en cache
  useEffect(() => {
    if (imageCache.has(src)) {
      const isLoaded = imageCache.get(src);
      if (isLoaded) {
        setImageSrc(src);
        setIsLoading(false);
      } else {
        setImageSrc(fallbackSrc);
        setIsLoading(false);
      }
      return;
    }
  }, [src, fallbackSrc]);

  // Intersection Observer pour lazy loading
  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.unobserve(element);
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [rootMargin, threshold]);

  // Charger l'image quand elle devient visible
  useEffect(() => {
    if (!isIntersecting || imageCache.has(src)) return;

    setIsLoading(true);
    
    const img = new Image();
    
    img.onload = () => {
      imageCache.set(src, true);
      setImageSrc(src);
      setIsLoading(false);
    };
    
    img.onerror = () => {
      imageCache.set(src, false);
      setImageSrc(fallbackSrc);
      setIsLoading(false);
    };
    
    img.src = src;
  }, [isIntersecting, src, fallbackSrc]);

  return {
    imgRef,
    imageSrc,
    isLoading,
    isIntersecting
  };
};